/**
 * Phase 2A — Shared, catalog-driven, permission-filtered Reference Picker.
 *
 * Replaces raw record-ID entry for domain `type:'ref'` fields. Searches
 * authorised, participant-scoped records via GET /careplan-domains/references,
 * shows safe labels + availability, disables unselectable records, and stores
 * the record id (backend save contract unchanged). Backward compatible with
 * manually-entered ids (shown with an "Unresolved" badge + keep/replace) and
 * degrades to a plain ID input when the kind is forbidden/deferred.
 *
 * Rendered only when `participant_careplan_reference_picker_enabled` is ON;
 * otherwise CareDomainDrawer renders the plain input (byte-identical fallback).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Loader2, Search, Check, AlertTriangle, X, Link2, RefreshCw, Pencil } from 'lucide-react';

const humanize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const AVAILABILITY_STYLE = {
  available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  inactive: 'bg-slate-100 text-slate-600 border-slate-200',
  expired: 'bg-amber-50 text-amber-700 border-amber-200',
  faulty: 'bg-red-50 text-red-700 border-red-200',
  recalled: 'bg-red-50 text-red-700 border-red-200',
  out_of_service: 'bg-red-50 text-red-700 border-red-200',
  overdue: 'bg-amber-50 text-amber-700 border-amber-200',
};

export function ReferenceField({
  participantId, fieldKey, refKind, label, required, value, canEdit, onChange,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forbidden, setForbidden] = useState(false);   // 403 or deferred (404 kind) → manual mode
  const [manual, setManual] = useState(false);
  const [resolvedLabel, setResolvedLabel] = useState('');
  const [includeUnavailable, setIncludeUnavailable] = useState(false);
  const debounceRef = useRef(null);
  const tid = `reffield-${fieldKey}`;

  const doSearch = useCallback(async (q, incl) => {
    if (!participantId || !refKind) return;
    setLoading(true); setError('');
    try {
      const r = await api.get('/careplan-domains/references', {
        params: { participant_id: participantId, kind: refKind, q: q || undefined, include_unavailable: !!incl, limit: 25 },
      });
      const results = r.data?.data?.results || [];
      setItems(results);
      // Backfill the collapsed label for an existing value if present in results.
      if (value && !resolvedLabel) {
        const hit = results.find(x => x.ref_id === value);
        if (hit) setResolvedLabel(hit.summary || '');
      }
    } catch (e) {
      const code = e?.response?.data?.error?.code || e?.response?.data?.code;
      const status = e?.response?.status;
      // Permanent fallback to manual only for authorisation/deferred/unknown kinds.
      if (status === 403 || code === 'REFERENCE_KIND_FORBIDDEN' ||
          code === 'REFERENCE_KIND_DEFERRED' || code === 'REFERENCE_KIND_UNKNOWN') {
        setForbidden(true); setManual(true); setOpen(false);
      } else {
        setError('Could not load records. Retry.');
      }
    } finally { setLoading(false); }
  }, [participantId, refKind, value, resolvedLabel]);

  // Debounced search while the popover is open.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query, includeUnavailable), 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [open, query, includeUnavailable, doSearch]);

  const select = (item) => {
    if (!item.selectable) return;
    onChange(item.ref_id);
    setResolvedLabel(item.summary || '');
    setOpen(false);
  };

  const clear = () => { onChange(''); setResolvedLabel(''); };

  // ── Manual / forbidden fallback: plain ID input (backend contract unchanged)
  if (manual) {
    return (
      <div className="space-y-1" key={fieldKey}>
        <Label>{label}{required ? ' *' : ''}</Label>
        <Input value={value || ''} onChange={e => onChange(e.target.value)} disabled={!canEdit}
               placeholder="Enter record ID" data-testid={`${tid}-manual`} />
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          {forbidden
            ? <span data-testid={`${tid}-forbidden-note`}>Picker unavailable for this reference — enter the ID manually.</span>
            : <span>Manual entry.</span>}
          {!forbidden && canEdit && (
            <button type="button" className="text-primary inline-flex items-center gap-1"
                    onClick={() => setManual(false)} data-testid={`${tid}-use-picker`}>
              <Search className="h-3 w-3" /> Use picker
            </button>
          )}
        </div>
      </div>
    );
  }

  const collapsedLabel = resolvedLabel || (value ? '' : '');

  return (
    <div className="space-y-1" key={fieldKey}>
      <Label>{label}{required ? ' *' : ''}</Label>
      <Popover open={open} onOpenChange={(o) => canEdit && setOpen(o)}>
        <PopoverTrigger asChild>
          <button type="button" disabled={!canEdit} data-testid={`${tid}-trigger`}
                  className="w-full flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm text-left disabled:opacity-70 hover:bg-accent/40 transition-colors">
            <span className="flex items-center gap-2 min-w-0">
              <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              {value ? (
                collapsedLabel
                  ? <span className="truncate" data-testid={`${tid}-selected-label`}>{collapsedLabel}</span>
                  : <span className="flex items-center gap-1 min-w-0">
                      <span className="truncate font-mono text-xs" data-testid={`${tid}-raw-id`}>{value}</span>
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200" data-testid={`${tid}-unresolved`}>Unresolved</Badge>
                    </span>
              ) : <span className="text-muted-foreground">Select {humanize(refKind)}…</span>}
            </span>
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" data-testid={`${tid}-popover`}>
          <div className="flex items-center border-b px-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input autoFocus value={query} onChange={e => setQuery(e.target.value)}
                   placeholder="Search…" data-testid={`${tid}-search`}
                   className="flex-1 bg-transparent px-2 py-2 text-sm outline-none" />
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>

          <div className="max-h-64 overflow-y-auto py-1" data-testid={`${tid}-results`}>
            {error && (
              <div className="px-3 py-4 text-center text-xs text-red-600" data-testid={`${tid}-error`}>
                {error}
                <button type="button" className="ml-2 inline-flex items-center gap-1 text-primary"
                        onClick={() => doSearch(query, includeUnavailable)} data-testid={`${tid}-retry`}>
                  <RefreshCw className="h-3 w-3" /> Retry
                </button>
              </div>
            )}
            {!error && !loading && items.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground" data-testid={`${tid}-empty`}>
                No matching records.
              </div>
            )}
            {!error && items.map(item => (
              <button type="button" key={item.ref_id} disabled={!item.selectable}
                      onClick={() => select(item)} data-testid={`${tid}-option-${item.ref_id}`}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm ${item.selectable ? 'hover:bg-accent cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}>
                <span className="flex items-center gap-2 min-w-0">
                  {value === item.ref_id && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                  <span className="truncate">{item.summary || '(no label)'}</span>
                </span>
                <span className="flex items-center gap-1 shrink-0">
                  {!item.selectable && item.reason && (
                    <span className="text-[10px] text-muted-foreground max-w-[120px] truncate" title={item.reason}>{item.reason}</span>
                  )}
                  <Badge variant="outline" className={`text-[10px] ${AVAILABILITY_STYLE[item.availability] || ''}`}>
                    {humanize(item.availability || item.status || '')}
                  </Badge>
                </span>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-2 border-t px-3 py-2 text-[11px]">
            <label className="flex items-center gap-1 cursor-pointer text-muted-foreground" data-testid={`${tid}-toggle-unavailable`}>
              <input type="checkbox" checked={includeUnavailable} onChange={e => setIncludeUnavailable(e.target.checked)} />
              Show unavailable
            </label>
            <button type="button" className="inline-flex items-center gap-1 text-primary"
                    onClick={() => { setManual(true); setOpen(false); }} data-testid={`${tid}-enter-manually`}>
              <Pencil className="h-3 w-3" /> Enter ID manually
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {value && canEdit && (
        <button type="button" onClick={clear} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-red-600"
                data-testid={`${tid}-clear`}>
          <X className="h-3 w-3" /> Clear link
        </button>
      )}
    </div>
  );
}

export default ReferenceField;
